import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import lib from '../../assets/utils/lib';
import { states } from '../../assets/utils/config';
import {
  Table,
  Input,
  Button,
  Select,
  Space,
  notification,
  Segmented,
  Spin,
  Alert,
  Empty,
  Pagination,
  Tag,
  Rate,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  TableOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FormOutlined,
  BankOutlined,
  UnorderedListOutlined,
  DownloadOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import './facilities-dashboard.css';
import DashboardHero from '../dashboard/components/DashboardHero';
import DashboardSection from '../dashboard/components/DashboardSection';
import { TableSkeleton } from '../dashboard/components/DashboardSkeleton';
import { hasMetricValue } from '../dashboard/utils';
import FacilityListingCard from './FacilityListingCard';
import FacilityOnboardingModal from './FacilityOnboardingModal';
import FacilityAccountsImportModal from './FacilityAccountsImportModal';
import { useCanManageFacilityAccounts } from './FacilityAccountDetails';
import FacilitySuggestions from './FacilitySuggestions';
import FacilitiesExportModal from '../../components/Facilities/FacilitiesExportModal';
import FacilitiesExportsDrawer from '../../components/Facilities/FacilitiesExportsDrawer';
import { canExportFacilities } from '../../components/Facilities/facilityExportHelpers';
import useLocalStorage from '../../assets/utils/useLocal';

const { Option } = Select;

const getFacilityId = (record) => record?.id ?? record?.facility_id;
const getFacilityName = (record) => record?.name ?? record?.facility_name ?? '—';
// Tier label the API already resolves (tiers.tier_name, or "All Plans" when unset).
const getFacilityTier = (record) =>
  [record?.facility_plan_type, record?.plan_type].find((v) => v != null && v !== '') ?? '—';

const facilityEncountersPath = (record) => {
  const id = getFacilityId(record);
  if (id == null || id === '') return null;
  return `/checkins?facility_id=${encodeURIComponent(id)}&status=0`;
};

const isFacilityActive = (record) =>
  record?.status === 1 || record?.status === '1' || record?.status === 'active';

const LEGACY_FACILITY_ROLES = [1, 3, 4, 5];

function FacilitiesList() {
  const { user } = useAuth();
  const { hasAnyPermission, hasAnyPermissionWithLegacy, canStayOnModule } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Permission-first: the legacy staff role ids only apply while the session has
  // no RBAC permission map, so a populated map can no longer be overridden.
  const canViewFacilitySuggestions = canStayOnModule('facilitySuggestions', LEGACY_FACILITY_ROLES);

  const section = searchParams.get('view') === 'suggestions' && canViewFacilitySuggestions
    ? 'suggestions'
    : 'directory';
  const setSection = useCallback((value) => {
    if (value === 'suggestions') setSearchParams({ view: 'suggestions' }, { replace: true });
    else setSearchParams({}, { replace: true });
  }, [setSearchParams]);
  const [suggestionsCount, setSuggestionsCount] = useState(0);

  const [facilities, setFacilities] = useState([]);
  // Filters, sort, and page hydrate from the URL so opening a facility and
  // coming Back lands on the same filtered list, not the full directory.
  const [filters, setFilters] = useState(() => ({
    search: searchParams.get('search') || '',
    state: searchParams.get('state') || '',
    status: searchParams.get('status') || '',
    // '' = every facility, 'main' = mains + standalone only, 'branch' = branches only
    branch_status: searchParams.get('branch_status') || '',
    facility_type_id: searchParams.get('facility_type_id') || '',
    facility_tier_id: searchParams.get('facility_tier_id') || '',
  }));
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort') || 'name');
  const [pagination, setPagination] = useState(() => ({
    current: Math.max(1, parseInt(searchParams.get('page'), 10) || 1),
    pageSize: Math.max(1, parseInt(searchParams.get('page_size'), 10) || 20),
    total: 0,
  }));
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  // List vs table is a standing preference, not per-visit state: opening a
  // facility and coming back must land on the view the user left.
  const [storedView, setView] = useLocalStorage('facility-directory-view', 'list');
  // A stale or unparseable stored value must not leave the toggle unset.
  const view = storedView === 'table' ? 'table' : 'list';
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [facilityExportOpen, setFacilityExportOpen] = useState(false);
  const [facilityExportsDrawerOpen, setFacilityExportsDrawerOpen] = useState(false);
  const canOnboardFacility = useMemo(
    () => hasAnyPermissionWithLegacy(['facilities.manage', 'admin.*'], [1, 2]),
    [hasAnyPermissionWithLegacy],
  );
  const canViewFacilityExports = canExportFacilities(user, hasAnyPermission, canStayOnModule);
  // Finance: bulk load of account details. Separate from "Add facility" (the facility bulk upload).
  const canImportAccountDetails = useCanManageFacilityAccounts();
  const [accountImportOpen, setAccountImportOpen] = useState(false);

  // The exact filter/sort params behind the list as it currently stands. The
  // detail page is handed a copy so "Next facility" can walk this same order.
  const listQuery = useMemo(() => ({
    ...(filters.search && { search: filters.search }),
    ...(filters.state && { state: filters.state }),
    ...(filters.status !== '' && { status: filters.status }),
    ...(filters.branch_status && { branch_status: filters.branch_status }),
    ...(filters.facility_type_id && { facility_type_id: filters.facility_type_id }),
    ...(filters.facility_tier_id && { facility_tier_id: filters.facility_tier_id }),
    // "newest" is created_at descending; every other key sorts ascending.
    ...(sortBy === 'newest'
      ? { sort_by: 'created_at', sort_dir: 'desc' }
      : { sort_by: sortBy }),
  }), [filters, sortBy]);

  const loadFacilities = useCallback(
    async (page = 1, limit = pagination.pageSize, showSearchLoading = false) => {
      if (showSearchLoading) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const params = { page, limit, ...listQuery };
        const resp = await lib.getFacilitiesV2(params);
        const body = resp?.data;
        const hasError = body && (Number(body.error) === 1 || body.error === true);
        if (body && !hasError && Array.isArray(body.data)) {
          setFacilities(body.data);
          setPagination((prev) => ({
            ...prev,
            current: body.pagination?.page ?? page,
            pageSize: body.pagination?.limit ?? limit,
            total: body.pagination?.total ?? 0,
          }));
        } else if (hasError || (body && !Array.isArray(body.data))) {
          const msg = body?.message || 'Failed to load facilities';
          setError(msg);
          setFacilities([]);
          setPagination((prev) => ({ ...prev, current: page, pageSize: limit, total: 0 }));
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to load facilities';
        setError(msg);
        notification.error({ message: msg, placement: 'topRight' });
        setFacilities([]);
        setPagination((prev) => ({ ...prev, current: page, pageSize: limit, total: 0 }));
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    },
    [listQuery, pagination.pageSize]
  );

  useEffect(() => {
    if (!user) return;
    if (!canStayOnModule('facilities', LEGACY_FACILITY_ROLES)) {
      navigate('/dashboard');
      return;
    }
    // Restore the page hydrated from the URL, not page 1 — this is what makes
    // Back from a facility land on the same slice of the list.
    loadFacilities(pagination.current, pagination.pageSize);
  }, [user, canStayOnModule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Skip this effect's mount run: the initial load above already used the
  // hydrated filters, and re-firing here would clobber the restored page.
  const filtersDidMount = useRef(false);
  useEffect(() => {
    if (!filtersDidMount.current) {
      filtersDidMount.current = true;
      return undefined;
    }
    if (user && canStayOnModule('facilities', LEGACY_FACILITY_ROLES)) {
      const t = setTimeout(() => loadFacilities(1, pagination.pageSize, true), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [filters, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror list state into the URL (replace, not push — Back should leave the
  // page, not step through filter keystrokes).
  useEffect(() => {
    const params = {};
    if (section === 'suggestions') params.view = 'suggestions';
    if (filters.search) params.search = filters.search;
    if (filters.state) params.state = filters.state;
    if (filters.status !== '') params.status = String(filters.status);
    if (filters.branch_status) params.branch_status = filters.branch_status;
    if (filters.facility_type_id) params.facility_type_id = String(filters.facility_type_id);
    if (filters.facility_tier_id) params.facility_tier_id = String(filters.facility_tier_id);
    if (sortBy !== 'name') params.sort = sortBy;
    if (pagination.current > 1) params.page = String(pagination.current);
    if (pagination.pageSize !== 20) params.page_size = String(pagination.pageSize);
    setSearchParams(params, { replace: true });
  }, [section, filters, sortBy, pagination.current, pagination.pageSize, setSearchParams]);

  useEffect(() => {
    lib.getAdminFacilityTypes()
      .then((resp) => {
        const rows = resp?.data?.data;
        setFacilityTypes(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {});
    lib.getAdminTiers({ scope: 'active' })
      .then((resp) => {
        const rows = resp?.data?.data;
        setTiers(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || !canViewFacilitySuggestions) {
      setSuggestionsCount(0);
      return;
    }
    lib.getPendingFacilitySuggestionsCount().then(setSuggestionsCount).catch(() => {});
  }, [user, canViewFacilitySuggestions, section]);

  const handleSearch = (value) => setFilters((prev) => ({ ...prev, search: value }));
  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters({
    search: '',
    state: '',
    status: '',
    branch_status: '',
    facility_type_id: '',
    facility_tier_id: '',
  });

  const hasActiveFilters = Boolean(
    filters.search
    || filters.state
    || filters.status !== ''
    || filters.branch_status
    || filters.facility_type_id
    || filters.facility_tier_id
  );

  const openFacility = useCallback((record) => {
    const id = getFacilityId(record);
    if (id == null || id === '') return;
    // Hand the detail page the slice of the directory it was opened from, so it
    // can step to the next facility in this exact order without coming back.
    navigate(`/facilities/${id}`, {
      state: {
        facility: record,
        listContext: {
          query: listQuery,
          // The directory's own query string, so Back can rebuild this exact
          // list even after the pager has walked onto another page.
          listUrl: searchParams.toString(),
          page: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          rows: facilities,
        },
      },
    });
  }, [navigate, listQuery, searchParams, pagination.current, pagination.pageSize, pagination.total, facilities]);

  const openEncounters = useCallback((record) => {
    const path = facilityEncountersPath(record);
    if (!path) return;
    navigate(path);
  }, [navigate]);

  const refreshCurrent = useCallback(() => {
    loadFacilities(pagination.current, pagination.pageSize);
  }, [loadFacilities, pagination.current, pagination.pageSize]);

  const activeOnPage = useMemo(
    () => facilities.filter(isFacilityActive).length,
    [facilities]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(pagination.total / pagination.pageSize)),
    [pagination.total, pagination.pageSize]
  );

  const heroMetrics = useMemo(() => [
    {
      key: 'total',
      title: 'Total facilities',
      value: pagination.total,
      color: '#3497D3',
      icon: <BankOutlined />,
      subtitle: hasActiveFilters ? 'Matching filters' : 'All facilities',
    },
    {
      key: 'page',
      title: 'On this page',
      value: facilities.length,
      color: '#722ed1',
      icon: <UnorderedListOutlined />,
      subtitle: `Page ${pagination.current} of ${totalPages}`,
    },
    {
      key: 'active',
      title: 'Active',
      value: activeOnPage,
      color: '#49B870',
      icon: <CheckCircleOutlined />,
      subtitle: 'On this page',
    },
    {
      key: 'inactive',
      title: 'Inactive',
      value: Math.max(0, facilities.length - activeOnPage),
      color: '#94a3b8',
      icon: <CloseCircleOutlined />,
      subtitle: 'On this page',
    },
  ].filter((m) => hasMetricValue(m.value) || m.key === 'page'), [
    pagination.total,
    pagination.current,
    totalPages,
    facilities.length,
    activeOnPage,
    hasActiveFilters,
  ]);

  const columns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <Space size={4} wrap>
          <Button
            type="link"
            className="facilities-name-link"
            onClick={(event) => {
              event.stopPropagation();
              openFacility(record);
            }}
          >
            {getFacilityName(record)}
          </Button>
          {record?.is_branch ? (
            <Tag color="blue" title={record.parent_facility_name || undefined}>
              Branch{record.parent_facility_name ? ` of ${record.parent_facility_name}` : ''}
            </Tag>
          ) : null}
          {!record?.is_branch && Number(record?.branch_count) > 0 ? (
            <Tag color="gold">
              {record.branch_count} branch{Number(record.branch_count) === 1 ? '' : 'es'}
            </Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Tier',
      key: 'tier',
      render: (_, record) => getFacilityTier(record),
    },
    {
      title: 'Type',
      dataIndex: 'facility_type',
      key: 'facility_type',
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (isFacilityActive(record) ? 'Active' : 'Inactive'),
    },
    {
      title: 'Rating',
      key: 'rating',
      render: (_, record) => {
        const rating = record?.rating;
        if (!rating || !rating.count) {
          return <span style={{ color: '#999' }}>No ratings</span>;
        }
        return (
          <Space size={4}>
            <Rate disabled allowHalf value={rating.average} style={{ fontSize: 14 }} />
            <span>{rating.average.toFixed(1)} ({rating.count})</span>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size={4} onClick={(event) => event.stopPropagation()}>
          <Button type="link" size="small" onClick={() => openEncounters(record)}>
            Encounters
          </Button>
        </Space>
      ),
    },
  ], [openEncounters, openFacility]);

  const emptyDescription = hasActiveFilters
    ? 'No facilities match your filters'
    : 'No facilities found';

  const paginationBlock = pagination.total > pagination.pageSize ? (
    <div className="facilities-pagination">
      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onChange={(page, pageSize) => loadFacilities(page, pageSize || pagination.pageSize)}
        onShowSizeChange={(_, size) => loadFacilities(1, size)}
        showSizeChanger
        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} facilities`}
        pageSizeOptions={['10', '20', '50', '100']}
      />
    </div>
  ) : null;

  return (
    <div className="facilities-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Facilities</h1>
            <p className="dash-masthead__meta">
              Manage and monitor healthcare provider facilities
            </p>
          </div>
        </header>
        <ModuleNav items={MODULE_NAVS.facilities} />

        {canViewFacilitySuggestions ? (
          <Segmented
            className="facilities-view-tabs"
            value={section}
            onChange={setSection}
            options={[
              { label: 'Directory', value: 'directory', icon: <BankOutlined /> },
              {
                label: suggestionsCount > 0 ? `Suggestions (${suggestionsCount})` : 'Suggestions',
                value: 'suggestions',
                icon: <FormOutlined />,
              },
            ]}
          />
        ) : null}

        {section === 'suggestions' && canViewFacilitySuggestions ? (
          <FacilitySuggestions embedded />
        ) : (
        <>
        <section className="facilities-overview dash-overview dash-overview--compact" aria-label="Facilities summary">
          <DashboardHero metrics={heroMetrics} loading={loading && !facilities.length && !pagination.total} />
        </section>

        {error ? (
          <Alert
            className="facilities-error-alert"
            message="Error loading facilities"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        ) : null}

        <DashboardSection
          id="facilities-directory"
          icon={<BankOutlined />}
          title="Facility directory"
          subtitle={
            searchLoading
              ? 'Searching…'
              : `${facilities.length} of ${pagination.total} · page ${pagination.current}`
          }
          collapsible={false}
          variant="content"
          action={(
            <div className="facilities-toolbar facilities-toolbar--inline">
              <Input
                className="facilities-toolbar__search"
                size="small"
                placeholder="Search facilities…"
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
              />
              <Select
                className="facilities-toolbar__select"
                size="small"
                placeholder="State"
                value={filters.state || undefined}
                onChange={(value) => handleFilterChange('state', value ?? '')}
                allowClear
              >
                {states.map((s) => (
                  <Option key={s.value} value={s.value}>{s.label}</Option>
                ))}
              </Select>
              <Select
                className="facilities-toolbar__select"
                size="small"
                placeholder="Branching"
                value={filters.branch_status || undefined}
                onChange={(value) => handleFilterChange('branch_status', value ?? '')}
                allowClear
                options={[
                  { value: 'main', label: 'Mains & standalone' },
                  { value: 'branch', label: 'Branches only' },
                ]}
              />
              <Select
                className="facilities-toolbar__select"
                size="small"
                placeholder="Type"
                value={filters.facility_type_id || undefined}
                onChange={(value) => handleFilterChange('facility_type_id', value ?? '')}
                allowClear
                options={facilityTypes.map((t) => ({ value: String(t.id), label: t.name }))}
              />
              <Select
                className="facilities-toolbar__select"
                size="small"
                placeholder="Tier"
                value={filters.facility_tier_id || undefined}
                onChange={(value) => handleFilterChange('facility_tier_id', value ?? '')}
                allowClear
                options={tiers.map((t) => ({ value: String(t.tier_id), label: t.tier_name }))}
              />
              <Select
                className="facilities-toolbar__select"
                size="small"
                placeholder="Status"
                value={filters.status === '' ? undefined : filters.status}
                onChange={(value) => handleFilterChange('status', value ?? '')}
                allowClear
                options={[
                  { value: '1', label: 'Active' },
                  { value: '0', label: 'Inactive' },
                ]}
              />
              <Select
                className="facilities-toolbar__select"
                size="small"
                value={sortBy}
                onChange={(value) => setSortBy(value ?? 'name')}
                options={[
                  { value: 'name', label: 'Sort: Name' },
                  { value: 'type', label: 'Sort: Type' },
                  { value: 'state', label: 'Sort: State' },
                  { value: 'newest', label: 'Sort: Newest' },
                ]}
              />
              <Button size="small" onClick={clearFilters} disabled={!hasActiveFilters}>
                Clear
              </Button>
              <Segmented
                size="small"
                value={view}
                onChange={setView}
                options={[
                  { label: 'List', value: 'list', icon: <AppstoreOutlined /> },
                  { label: 'Table', value: 'table', icon: <TableOutlined /> },
                ]}
              />
              <Button size="small" icon={<ReloadOutlined />} onClick={refreshCurrent} loading={loading}>
                Refresh
              </Button>
              {canViewFacilityExports ? (
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => setFacilityExportsDrawerOpen(true)}
                >
                  Exports
                </Button>
              ) : null}
              {canViewFacilityExports ? (
                <Button
                  size="small"
                  icon={<ExportOutlined />}
                  onClick={() => setFacilityExportOpen(true)}
                >
                  Export
                </Button>
              ) : null}
              {canImportAccountDetails ? (
                <Button size="small" icon={<BankOutlined />} onClick={() => setAccountImportOpen(true)}>
                  Import account details
                </Button>
              ) : null}
              {canOnboardFacility ? (
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setShowOnboarding(true)}>
                  Add facility
                </Button>
              ) : null}
            </div>
          )}
        >
          {loading && !facilities.length ? (
            view === 'table' ? (
              <TableSkeleton rows={8} columns={5} />
            ) : (
              <div className="facilities-loading">
                <Spin size="large" />
              </div>
            )
          ) : facilities.length === 0 ? (
            <Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : view === 'table' ? (
            <>
              <div className="facilities-table-wrap">
                <Table
                  columns={columns}
                  dataSource={facilities}
                  rowKey={(r) => getFacilityId(r) || r.facility_id}
                  pagination={false}
                  onRow={(record) => ({
                    onClick: () => openFacility(record),
                    style: { cursor: 'pointer' },
                  })}
                />
              </div>
              {paginationBlock}
            </>
          ) : (
            <>
              <div className="facilities-listing-stack">
                {facilities.map((facility) => (
                  <FacilityListingCard
                    key={getFacilityId(facility) || facility.facility_name}
                    record={facility}
                    onClick={() => openFacility(facility)}
                    actions={
                      <Button
                        type="link"
                        size="small"
                        className="listing-card__action"
                        onClick={() => openEncounters(facility)}
                      >
                        Encounters
                      </Button>
                    }
                  />
                ))}
              </div>
              {paginationBlock}
            </>
          )}
        </DashboardSection>
        </>
        )}
      </div>

      <FacilityAccountsImportModal
        open={accountImportOpen}
        onClose={() => setAccountImportOpen(false)}
      />

      <FacilityOnboardingModal
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onSuccess={() => loadFacilities(1, pagination.pageSize)}
        user={user}
      />

      <FacilitiesExportModal
        open={facilityExportOpen}
        onClose={() => setFacilityExportOpen(false)}
        search={filters.search}
        estimatedRows={pagination.total || null}
        onViewExports={() => {
          setFacilityExportOpen(false);
          setFacilityExportsDrawerOpen(true);
        }}
      />

      <FacilitiesExportsDrawer
        open={facilityExportsDrawerOpen}
        onClose={() => setFacilityExportsDrawerOpen(false)}
      />
    </div>
  );
}

export default FacilitiesList;
